import { useCallback, useState } from 'react'

/** A camera on the map: a circle, a 16:9 preview above its fix, or filling the map. */
export type CameraSphereMode = 'orb' | 'peek' | 'full'

type Expanded = { id: string; mode: Exclude<CameraSphereMode, 'orb'> } | null

export type ChangeCameraMode = (id: string, next: CameraSphereMode, onlyFrom?: CameraSphereMode[]) => void

/**
 * At most one camera on a map is expanded at a time. `onlyFrom` guards delayed
 * changes (hover timers, blur) so they never undo a newer, deliberate one.
 */
export function useExpandedCamera(ids: string[]) {
  const [expanded, setExpanded] = useState<Expanded>(null)
  // A camera that leaves the map takes its preview with it.
  const current = expanded && ids.includes(expanded.id) ? expanded : null
  if (expanded && !current) setExpanded(null)

  const change = useCallback<ChangeCameraMode>((id, next, onlyFrom) => {
    if (!ids.includes(id)) return
    setExpanded((prev) => {
      const from: CameraSphereMode = prev?.id === id ? prev.mode : 'orb'
      if (from === next || (onlyFrom && !onlyFrom.includes(from))) return prev
      if (next === 'orb') return prev?.id === id ? null : prev
      // Hovering another camera never takes over a camera filling the map.
      if (prev?.mode === 'full' && prev.id !== id) return prev
      return { id, mode: next }
    })
  }, [ids])

  const modeOf = (id: string): CameraSphereMode => (current?.id === id ? current.mode : 'orb')
  return { expanded: current, modeOf, change }
}

/**
 * An id as a quoted CSS attribute value, without `CSS.escape`: a quote and a backslash are escaped, and the three line
 * breaks a CSS string can't hold raw (newline, carriage return, form feed) are written as hex escapes.
 */
const LINE_BREAKS: Record<string, string> = { '\n': '\\a ', '\r': '\\d ', '\f': '\\c ' }
const quoted = (id: string) => `"${id.replace(/["\\]/g, '\\$&').replace(/[\n\r\f]/g, (character) => LINE_BREAKS[character])}"`

/** The camera's circle or preview inside a map stage: what the full view grows from. */
export function cameraFrame(stage: ParentNode | null | undefined, id: string) {
  return stage?.querySelector(`[data-camera-frame=${quoted(id)}]`) ?? null
}

/**
 * Return focus to a camera once its full view has closed and it is visible again (on the next frame). Every place the
 * camera stands in `stage` is tried in document order until one takes focus: its sphere (a Mapbox sphere hidden under
 * the play world can't), and a play world pin's own button (`data-camera-pin`) where its sphere is disabled. A camera
 * that can't take it anywhere (offline on the Mapbox map, or gone) hands it to `fallback`, such as the map itself.
 */
export function focusCamera(stage: ParentNode | null | undefined, id: string, fallback?: () => HTMLElement | null | undefined) {
  requestAnimationFrame(() => {
    const places = stage?.querySelectorAll<HTMLElement>(`[data-camera-sphere=${quoted(id)}], [data-camera-pin=${quoted(id)}]`) ?? []
    for (const place of places) {
      place.focus({ preventScroll: true })
      if (document.activeElement === place) return
    }
    fallback?.()?.focus({ preventScroll: true })
  })
}
